import React, { useCallback, useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import api from "../services/api";
import {
  dailyMediaErrorMessage,
  openVideoCallSettings,
  requestVideoCallPermissions,
} from "../services/videoCallPermissions";
import { isAndroidPlatform } from "../services/microphone";

export default function VideoCallModal({ mode, targetUserId, targetName, appel, onClose }) {
  const [phase, setPhase] = useState(mode === "outgoing" ? "preparing" : "incoming");
  const [callId, setCallId] = useState(appel?.call_id || null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [resolvedMsg, setResolvedMsg] = useState("");
  const [error, setError] = useState("");
  const [canOpenSettings, setCanOpenSettings] = useState(false);
  const [connectionState, setConnectionState] = useState("");
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const pollRef = useRef(null);
  const closeTimerRef = useRef(null);
  const closedRef = useRef(false);
  const acceptPendingRef = useRef(false);
  const callIdRef = useRef(callId);

  const callerName = mode === "outgoing" ? targetName : appel?.enseignant_nom;

  useEffect(() => { callIdRef.current = callId; }, [callId]);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (frameRef.current) {
      frameRef.current.destroy();
      frameRef.current = null;
    }
  }, []);

  const closeOnce = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  const closeAfter = useCallback((message, delay = 2000) => {
    setResolvedMsg(message);
    setPhase("resolved");
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    closeTimerRef.current = setTimeout(closeOnce, delay);
  }, [closeOnce]);

  const prepareNativeMedia = useCallback(async () => {
    try {
      setCanOpenSettings(false);
      await requestVideoCallPermissions();
      return true;
    } catch (permissionError) {
      setCanOpenSettings(isAndroidPlatform);
      setError(permissionError?.message || dailyMediaErrorMessage(permissionError));
      return false;
    }
  }, []);

  useEffect(() => {
    if (mode !== "outgoing") return undefined;
    let cancelled = false;
    const initiate = async () => {
      const allowed = await prepareNativeMedia();
      if (!allowed || cancelled) return;
      setPhase("calling");
      try {
        const response = await api.post("appels/initier/", { participant_id: targetUserId });
        if (cancelled) return;
        setCallId(response.data.call_id);
        setRoomInfo({ roomUrl: response.data.room_url, token: response.data.token });
      } catch (requestError) {
        if (!cancelled) setError(requestError.response?.data?.detail || "Impossible de démarrer l’appel.");
      }
    };
    initiate();
    return () => { cancelled = true; };
  }, [mode, prepareNativeMedia, targetUserId]);

  useEffect(() => {
    if (!callId || phase === "in-call" || phase === "resolved") return undefined;
    const poll = () => {
      api.get(`appels/${callId}/statut/`).then(response => {
        const status = response.data.statut;
        if (status === "accepted" && mode === "outgoing" && roomInfo) {
          setPhase("in-call");
        } else if (status === "declined") {
          closeAfter("Appel refusé.");
        } else if (status === "missed") {
          closeAfter("Appel non répondu.");
        } else if (status === "cancelled") {
          closeAfter("L’appel a été annulé.");
        }
      }).catch(() => {});
    };
    pollRef.current = setInterval(poll, 3000);
    const onVisible = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [callId, closeAfter, mode, phase, roomInfo]);

  useEffect(() => {
    if (phase !== "in-call" || !roomInfo || !containerRef.current || frameRef.current) return undefined;
    setConnectionState("Connexion à l’appel…");
    const frame = DailyIframe.createFrame(containerRef.current, {
      showLeaveButton: true,
      showFullscreenButton: true,
      showScreenShareButton: mode === "outgoing",
      iframeStyle: { width: "100%", height: "100%", border: "0" },
    });
    frameRef.current = frame;
    frame.on("joined-meeting", () => setConnectionState("Connecté"));
    frame.on("left-meeting", () => {
      const id = callIdRef.current;
      if (id) api.post(`appels/${id}/terminer/`).catch(() => {});
      closeOnce();
    });
    frame.on("camera-error", event => {
      setCanOpenSettings(isAndroidPlatform);
      setError(dailyMediaErrorMessage(event));
    });
    frame.on("error", event => setError(dailyMediaErrorMessage(event)));
    frame.on("network-connection", event => {
      const state = event?.event || event?.type;
      if (state === "interrupted") setConnectionState("Connexion interrompue — reconnexion…");
      if (state === "connected") setConnectionState("Connecté");
    });
    frame.join({ url: roomInfo.roomUrl, token: roomInfo.token }).catch(joinError => {
      setCanOpenSettings(isAndroidPlatform);
      setError(dailyMediaErrorMessage(joinError));
    });
    return undefined;
  }, [closeOnce, mode, phase, roomInfo]);

  useEffect(() => () => cleanup(), [cleanup]);

  const handleAccept = async () => {
    if (acceptPendingRef.current) return;
    acceptPendingRef.current = true;
    setError("");
    const allowed = await prepareNativeMedia();
    if (!allowed) {
      acceptPendingRef.current = false;
      return;
    }
    setPhase("preparing");
    try {
      const response = await api.post(`appels/${callId}/accepter/`);
      setRoomInfo({ roomUrl: response.data.room_url, token: response.data.token });
      setPhase("in-call");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Impossible de rejoindre l’appel.");
      setPhase("incoming");
      acceptPendingRef.current = false;
    }
  };

  const handleDecline = async () => {
    try { await api.post(`appels/${callId}/refuser/`); } catch (_) {}
    closeOnce();
  };

  const handleCancel = async () => {
    const id = callIdRef.current;
    if (id) {
      try { await api.post(`appels/${id}/terminer/`); } catch (_) {}
    }
    closeOnce();
  };

  if (phase === "in-call") {
    return (
      <div className="ql-call-overlay" role="dialog" aria-modal="true" aria-label="Appel vidéo">
        <div className="ql-call-live-status" aria-live="polite">{connectionState}</div>
        {error && <div className="ql-call-media-error">{error}</div>}
        <div ref={containerRef} className="ql-call-iframe-wrap" />
      </div>
    );
  }

  return (
    <div className="ql-call-overlay" role="dialog" aria-modal="true" aria-label="Appel vidéo">
      <div className="ql-call-panel">
        <div className="ql-call-avatar"><i className="bi bi-person-circle" /></div>
        <div className="ql-call-name">{callerName}</div>
        {error ? (
          <>
            <div className="ql-call-status text-danger">{error}</div>
            <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
              {canOpenSettings && (
                <button className="btn btn-outline-light btn-sm" onClick={openVideoCallSettings}>Ouvrir les paramètres</button>
              )}
              <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>Fermer</button>
            </div>
          </>
        ) : phase === "resolved" ? (
          <div className="ql-call-status">{resolvedMsg}</div>
        ) : phase === "preparing" ? (
          <div className="ql-call-status"><span className="spinner-border spinner-border-sm me-2" />Préparation de l’appel…</div>
        ) : phase === "calling" ? (
          <>
            <div className="ql-call-status"><span className="spinner-border spinner-border-sm me-2" />Appel en cours…</div>
            <button className="btn btn-danger btn-sm mt-3" onClick={handleCancel}><i className="bi bi-telephone-x me-1" />Annuler</button>
          </>
        ) : (
          <>
            <div className="ql-call-status">vous appelle en visio…</div>
            <div className="d-flex gap-3 mt-3">
              <button className="btn btn-danger btn-sm" onClick={handleDecline}><i className="bi bi-telephone-x me-1" />Refuser</button>
              <button className="btn btn-success btn-sm" onClick={handleAccept}><i className="bi bi-telephone-fill me-1" />Accepter</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
